import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, Star, MoreHorizontal, Image as ImageIcon, Sparkles, Upload, X, Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  apiListReviews, apiReviewsBulk, apiDeleteReview, apiGetSettings, apiUpdateSettings, errMsg,
  apiSearchProducts, apiGenerateReviews, apiUploadImage, apiDeleteAllReviews,
} from "../adminApi";
import { ReviewDialog } from "../components/ReviewDialog";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const COUNT_PRESETS = [10, 25, 50, 100];

function Stars({ n }) {
  const val = Number(n) || 0;
  return (
    <span className="inline-flex">
      {[1, 2, 3, 4, 5].map((i) => {
        const full = i <= Math.floor(val);
        const half = !full && val >= i - 0.5;
        return (
          <span key={i} className="relative inline-block h-3.5 w-3.5">
            <Star className="absolute inset-0 h-3.5 w-3.5 text-tertiary" />
            {(full || half) && (
              <span className={`absolute inset-0 overflow-hidden ${half ? "w-1/2" : "w-full"}`}>
                <Star className="h-3.5 w-3.5 fill-lime text-lime" />
              </span>
            )}
          </span>
        );
      })}
    </span>
  );
}

export default function Reviews() {
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [stars, setStars] = useState("all");
  const [status, setStatus] = useState("all");
  const [hasPhotos, setHasPhotos] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [policy, setPolicy] = useState("");

  // --- Generatore recensioni AI (multi-prodotto) ---
  const [genProducts, setGenProducts] = useState([]); // [{id, name}]
  const [genQuery, setGenQuery] = useState("");
  const [genResults, setGenResults] = useState([]);
  const [genCount, setGenCount] = useState(50);
  const [genPhotos, setGenPhotos] = useState([]);
  const [genUploading, setGenUploading] = useState(false);
  const [genLoading, setGenLoading] = useState(false);
  const [genProgress, setGenProgress] = useState(null); // {done, total, name, created}

  // --- Conferma eliminazioni ---
  const [confirm, setConfirm] = useState(null); // {title, desc, actionLabel, onConfirm}

  useEffect(() => {
    if (genQuery.trim().length < 2) { setGenResults([]); return; }
    const t = setTimeout(() => apiSearchProducts(genQuery.trim()).then(setGenResults).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [genQuery]);

  const addProduct = (p) => {
    setGenProducts((list) => (list.some((x) => x.id === p.id) ? list : [...list, { id: p.id, name: p.name }]));
    setGenResults([]); setGenQuery("");
  };
  const removeProduct = (id) => setGenProducts((list) => list.filter((x) => x.id !== id));

  const onGenPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setGenUploading(true);
    try {
      for (const f of files) {
        const res = await apiUploadImage(f, "rinova/reviews");
        if (res?.url) setGenPhotos((p) => [...p, res.url]);
      }
      toast.success("Foto caricate");
    } catch (err) { toast.error(errMsg(err)); }
    finally { setGenUploading(false); e.target.value = ""; }
  };

  const runGenerate = async () => {
    if (genProducts.length === 0) { toast.error("Scegli almeno un prodotto."); return; }
    const count = Math.max(1, Math.min(100, Number(genCount) || 1));
    setGenLoading(true);
    setGenProgress({ done: 0, total: genProducts.length, name: genProducts[0]?.name || "", created: 0 });
    let totalCreated = 0;
    let failed = 0;
    for (let i = 0; i < genProducts.length; i++) {
      const p = genProducts[i];
      setGenProgress({ done: i, total: genProducts.length, name: p.name, created: totalCreated });
      try {
        const res = await apiGenerateReviews({ product_id: p.id, count, photos: genPhotos });
        totalCreated += res.created || 0;
      } catch (err) { failed += 1; toast.error(`${p.name}: ${errMsg(err)}`); }
      setGenProgress({ done: i + 1, total: genProducts.length, name: p.name, created: totalCreated });
    }
    setGenLoading(false);
    setGenProgress(null);
    if (totalCreated > 0) {
      toast.success(`${totalCreated} recensioni create su ${genProducts.length - failed} prodotti`);
      setGenPhotos([]);
      load();
    } else if (failed === genProducts.length) {
      toast.error("Generazione non riuscita. Riprova.");
    }
  };

  const load = () => {
    const params = {};
    if (search) params.search = search;
    if (stars !== "all") params.stars = stars;
    if (status !== "all") params.status = status;
    if (hasPhotos !== "all") params.has_photos = hasPhotos;
    apiListReviews(params).then((d) => { setItems(d); setSelected([]); }).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search, stars, status, hasPhotos]);
  useEffect(() => { apiGetSettings().then((s) => setPolicy(s.reviews_policy_text || "")).catch(() => {}); }, []);

  const allChecked = items && items.length > 0 && selected.length === items.length;
  const toggleAll = () => setSelected(allChecked ? [] : items.map((i) => i.id));
  const toggleOne = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const runBulk = async (action) => {
    try { await apiReviewsBulk(selected, action); toast.success("Fatto"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const savePolicy = async () => {
    try { await apiUpdateSettings({ reviews_policy_text: policy }); toast.success("Testo salvato"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const doDeleteAll = async (payload) => {
    try {
      const res = await apiDeleteAllReviews(payload);
      toast.success(`${res.deleted} recensioni eliminate`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setConfirm(null); }
  };

  const askDeleteGenerated = () => setConfirm({
    title: "Eliminare tutte le recensioni generate?",
    desc: "Verranno rimosse definitivamente tutte le recensioni create con il generatore AI. Le recensioni inserite a mano restano.",
    actionLabel: "Elimina tutte le generate",
    onConfirm: () => doDeleteAll({ scope: "generated" }),
  });
  const askDeleteForProducts = () => setConfirm({
    title: `Eliminare le recensioni di ${genProducts.length} prodotti?`,
    desc: "Verranno rimosse definitivamente TUTTE le recensioni (generate e manuali) dei prodotti selezionati qui sopra.",
    actionLabel: "Elimina recensioni",
    onConfirm: () => doDeleteAll({ scope: "product", product_ids: genProducts.map((p) => p.id) }),
  });

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight sm:text-3xl">Recensioni</h1>
          <p className="mt-1 text-sm text-steel">{items?.length ?? 0} recensioni</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={askDeleteGenerated} data-testid="reviews-delete-generated">
            <Trash2 className="h-4 w-4" /> Elimina generate
          </Button>
          <Button variant="lime" size="sm" onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="review-new-button"><Plus className="h-4 w-4" /> Aggiungi recensione</Button>
        </div>
      </div>

      {/* Generatore recensioni AI */}
      <Card className="border-lime/30 bg-surface2" data-testid="review-generator-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Sparkles className="h-4 w-4 text-lime" /> Generatore recensioni AI
          </CardTitle>
          <p className="text-sm text-steel">
            Seleziona uno o più prodotti e crea fino a <strong className="text-chalk">100 recensioni per prodotto</strong> con un clic.
            Testi realistici e tutti diversi (slang, qualche errore, titoli unici), badge cliente Top, risposte del negozio e foto casuali.
          </p>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {/* Prodotti (multi-selezione) */}
          <div className="flex flex-col gap-1.5">
            <Label>Prodotti</Label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-tertiary" />
              <Input value={genQuery} onChange={(e) => setGenQuery(e.target.value)} placeholder="Cerca e aggiungi un prodotto…" className="pl-9" data-testid="review-gen-product-search" />
              {genResults.length > 0 && (
                <div className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-line bg-surface2 shadow-xl">
                  {genResults.map((p) => (
                    <button key={p.id} type="button" onClick={() => addProduct(p)}
                      className="block w-full px-3 py-2 text-left text-sm text-chalk hover:bg-surface3" data-testid={`review-gen-result-${p.id}`}>
                      {p.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {genProducts.length > 0 ? (
              <div className="mt-1 flex flex-wrap gap-2" data-testid="review-gen-selected-products">
                {genProducts.map((p) => (
                  <span key={p.id} className="inline-flex items-center gap-1.5 rounded-full border border-line bg-surface3 px-3 py-1 text-xs text-chalk">
                    <span className="max-w-[220px] truncate">{p.name}</span>
                    <button type="button" onClick={() => removeProduct(p.id)} aria-label="Rimuovi" className="text-steel hover:text-destructive"><X className="h-3.5 w-3.5" /></button>
                  </span>
                ))}
                <button type="button" onClick={() => setGenProducts([])} className="text-xs text-steel underline hover:text-chalk">Svuota</button>
              </div>
            ) : (
              <p className="text-xs text-steel">Nessun prodotto selezionato.</p>
            )}
          </div>

          {/* Quantità per prodotto */}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="gen-count">Quante per prodotto (1-100)</Label>
            <div className="flex flex-wrap items-center gap-2">
              <Input id="gen-count" type="number" min={1} max={100} value={genCount}
                onChange={(e) => setGenCount(e.target.value)} className="w-28" data-testid="review-gen-count" />
              <div className="flex gap-1.5">
                {COUNT_PRESETS.map((n) => (
                  <Button key={n} type="button" variant={Number(genCount) === n ? "lime" : "secondary"} size="sm"
                    onClick={() => setGenCount(n)} data-testid={`review-gen-preset-${n}`}>{n}</Button>
                ))}
              </div>
            </div>
          </div>

          {/* Foto opzionali */}
          <div className="flex flex-col gap-2">
            <Label>Foto (opzionali) — aggiunte a caso ad alcune recensioni</Label>
            <div className="flex flex-wrap items-center gap-2">
              {genPhotos.map((u, i) => (
                <div key={i} className="relative h-16 w-16 overflow-hidden rounded-lg border border-line bg-surface3">
                  <img src={u} alt="" className="h-full w-full object-cover" />
                  <button type="button" onClick={() => setGenPhotos((p) => p.filter((_, j) => j !== i))}
                    className="absolute right-0.5 top-0.5 rounded-full bg-ink/80 p-0.5" aria-label="Rimuovi foto">
                    <X className="h-3 w-3 text-chalk" />
                  </button>
                </div>
              ))}
              <label className="flex h-16 w-16 cursor-pointer items-center justify-center rounded-lg border border-dashed border-stroke text-steel hover:border-lime hover:text-lime" data-testid="review-gen-photo-upload">
                {genUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                <input type="file" accept="image/*" multiple className="hidden" onChange={onGenPhotos} disabled={genUploading} />
              </label>
            </div>
          </div>

          {/* Barra di avanzamento */}
          {genProgress && (
            <div className="rounded-lg border border-lime/30 bg-surface3 px-3 py-2 text-sm text-chalk" data-testid="review-gen-progress">
              Generazione… prodotto {Math.min(genProgress.done + 1, genProgress.total)}/{genProgress.total}
              {genProgress.name ? ` — ${genProgress.name}` : ""} · {genProgress.created} create finora
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Button variant="lime" onClick={runGenerate} disabled={genLoading || genProducts.length === 0} data-testid="review-gen-run-button">
              {genLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {genLoading ? "Generazione in corso…" : `Genera${genProducts.length ? ` (${genProducts.length}×${Math.max(1, Math.min(100, Number(genCount) || 1))})` : ""}`}
            </Button>
            {genProducts.length > 0 && !genLoading && (
              <Button variant="ghost" className="text-destructive hover:text-destructive" onClick={askDeleteForProducts} data-testid="review-gen-delete-products">
                <Trash2 className="h-4 w-4" /> Elimina recensioni di questi prodotti
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-col gap-3 rounded-xl border border-line bg-surface2 p-3 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-tertiary" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca per nome o titolo" className="pl-9" data-testid="reviews-search" />
        </div>
        <Select value={stars} onValueChange={setStars}><SelectTrigger className="w-full sm:w-[130px]"><SelectValue placeholder="Stelle" /></SelectTrigger><SelectContent>
          <SelectItem value="all">Tutte le stelle</SelectItem>{[5, 4, 3, 2, 1].map((n) => <SelectItem key={n} value={String(n)}>{n} stelle</SelectItem>)}</SelectContent></Select>
        <Select value={status} onValueChange={setStatus}><SelectTrigger className="w-full sm:w-[150px]"><SelectValue placeholder="Stato" /></SelectTrigger><SelectContent>
          <SelectItem value="all">Tutti gli stati</SelectItem><SelectItem value="PUBLISHED">Pubblicate</SelectItem><SelectItem value="HIDDEN">Nascoste</SelectItem></SelectContent></Select>
        <Select value={hasPhotos} onValueChange={setHasPhotos}><SelectTrigger className="w-full sm:w-[130px]"><SelectValue placeholder="Foto" /></SelectTrigger><SelectContent>
          <SelectItem value="all">Con o senza foto</SelectItem><SelectItem value="yes">Con foto</SelectItem></SelectContent></Select>
      </div>

      {selected.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-stroke bg-surface3 px-3 py-2" data-testid="reviews-bulk-bar">
          <span className="text-sm text-chalk">{selected.length} selezionate</span>
          <div className="ml-auto flex gap-2">
            <Button variant="secondary" size="sm" onClick={() => runBulk("publish")}>Pubblica</Button>
            <Button variant="secondary" size="sm" onClick={() => runBulk("hide")}>Nascondi</Button>
            <Button variant="ghost" size="sm" className="text-destructive" onClick={() => runBulk("delete")} data-testid="reviews-bulk-delete">Elimina</Button>
          </div>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-line bg-surface2">
        <Table data-testid="reviews-table">
          <TableHeader>
            <TableRow className="border-line hover:bg-transparent">
              <TableHead className="w-10"><Checkbox checked={allChecked} onCheckedChange={toggleAll} aria-label="Seleziona tutto" /></TableHead>
              <TableHead>Prodotto</TableHead><TableHead>Stelle</TableHead><TableHead>Nome</TableHead>
              <TableHead>Data</TableHead><TableHead>Foto</TableHead><TableHead>Stato</TableHead><TableHead className="w-10"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!items ? (
              Array.from({ length: 5 }).map((_, i) => <TableRow key={i} className="border-line"><TableCell colSpan={8}><Skeleton className="h-8 w-full" /></TableCell></TableRow>)
            ) : items.length === 0 ? (
              <TableRow className="border-line"><TableCell colSpan={8} className="py-10 text-center text-sm text-steel">Nessuna recensione.</TableCell></TableRow>
            ) : items.map((r) => (
              <TableRow key={r.id} className="border-line cursor-pointer" onClick={() => { setEditing(r); setDialogOpen(true); }}>
                <TableCell onClick={(e) => e.stopPropagation()}><Checkbox checked={selected.includes(r.id)} onCheckedChange={() => toggleOne(r.id)} /></TableCell>
                <TableCell className="text-chalk">{r.product_name}</TableCell>
                <TableCell><Stars n={r.stars} /></TableCell>
                <TableCell className="text-steel">{r.display_name}{r.verified && <span className="ml-1 text-[10px] text-lime">✓</span>}</TableCell>
                <TableCell className="text-steel">{fmtDate(r.date)}</TableCell>
                <TableCell className="text-steel"><span className="inline-flex items-center gap-1"><ImageIcon className="h-3.5 w-3.5" />{(r.photos || []).length}</span></TableCell>
                <TableCell><span className={`rounded-full px-2 py-0.5 text-xs ${r.status === "PUBLISHED" ? "bg-lime/15 text-lime" : "bg-surface3 text-steel"}`}>{r.status === "PUBLISHED" ? "Pubblicata" : "Nascosta"}</span></TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => { setEditing(r); setDialogOpen(true); }}>Modifica</DropdownMenuItem>
                      <DropdownMenuItem className="text-destructive" onClick={async () => { await apiDeleteReview(r.id); toast.success("Eliminata"); load(); }}>Elimina</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Card className="border-line bg-surface2">
        <CardHeader><CardTitle className="text-base">Testo "Come verifichiamo le recensioni"</CardTitle></CardHeader>
        <CardContent className="flex flex-col gap-3">
          <Textarea rows={3} value={policy} onChange={(e) => setPolicy(e.target.value)} data-testid="reviews-policy-text" />
          <Button variant="secondary" size="sm" className="self-start" onClick={savePolicy} data-testid="reviews-policy-save">Salva testo</Button>
        </CardContent>
      </Card>

      <ReviewDialog open={dialogOpen} onOpenChange={setDialogOpen} review={editing} onSaved={load} />

      <AlertDialog open={!!confirm} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
        <AlertDialogContent data-testid="reviews-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirm?.desc}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-white hover:bg-destructive/90" onClick={() => confirm?.onConfirm?.()} data-testid="reviews-confirm-action">
              {confirm?.actionLabel || "Elimina"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
